/**
 * Shopify Product Changes Service
 * 
 * This service handles applying changes to product input data before creating or updating products in Shopify.
 * It provides methods to apply various types of changes to product data, including metafields, variants, and options.
 */

export class ShopifyProductChangesService {
  constructor() {
    // No initialization needed
  }

  /**
   * Applies changes to product input data
   * @param {object} input - The product input data to modify
   * @param {object} changes - Changes to apply to the product
   * @returns {object} - Modified product input data
   */
  applyChanges(input, changes) {
    if (!changes) return input;
    
    console.log('Applying changes to product input:', Object.keys(changes));
    
    // Apply basic product field changes
    this.applyBasicChanges(input, changes);
    
    // Apply option changes
    this.applyOptionChanges(input, changes);
    
    // Apply metafield changes
    this.applyMetafieldChanges(input, changes);
    
    // Apply variant changes
    this.applyVariantChanges(input, changes);
    
    return input;
  }

  /**
   * Applies basic changes to product fields
   * @param {object} input - The product input data to modify
   * @param {object} changes - Changes to apply to the product
   */
  applyBasicChanges(input, changes) {
    // Special handling for product type to ensure it's updated correctly
    // Check for both 'productType' and 'product_type' in changes
    if (changes.productType !== undefined) {
      input.productType = changes.productType;
      console.log(`Updated product type to: ${changes.productType}`);
    } else if (changes.product_type !== undefined) {
      input.productType = changes.product_type;
      console.log(`Updated product type from product_type to: ${changes.product_type}`);
    }
    
    // Special handling for image alt text
    if (changes.image_alt_text !== undefined) {
      // Apply the updated alt text to all images
      input.images.forEach(image => {
        image.alt = changes.image_alt_text;
      });
      console.log(`Updated alt text for all images to: ${changes.image_alt_text}`);
    }
    
    // Handle other product fields
    Object.entries(changes).forEach(([key, value]) => {
      // Skip null or undefined values
      if (value === null || value === undefined) {
        return;
      }
      
      if (key === 'title') input.title = value;
      if (key === 'descriptionHtml' || key === 'body_html') input.descriptionHtml = value;
      // productType is already handled above
      if (key === 'vendor') input.vendor = value;
      if (key === 'tags') input.tags = value;
      if (key === 'seo_title') {
        if (!input.seo) input.seo = {};
        input.seo.title = value;
      }
      if (key === 'seo_description') {
        if (!input.seo) input.seo = {};
        input.seo.description = value;
      }
    });
  }

  /**
   * Applies option changes to product input data
   * @param {object} input - The product input data to modify
   * @param {object} changes - Changes to apply to the product
   */
  applyOptionChanges(input, changes) {
    // Special handling for option names
    if (changes.option1_name !== undefined && input.options.length >= 1) {
      input.options[0].name = changes.option1_name;
      console.log(`Updated option 1 name to: ${changes.option1_name}`);
    }
    
    if (changes.option2_name !== undefined && input.options.length >= 2) {
      input.options[1].name = changes.option2_name;
      console.log(`Updated option 2 name to: ${changes.option2_name}`);
    }
    
    if (changes.option3_name !== undefined && input.options.length >= 3) {
      input.options[2].name = changes.option3_name;
      console.log(`Updated option 3 name to: ${changes.option3_name}`);
    }
    
    // Special handling for option values
    if (changes.option1_values && input.options.length >= 1) {
      // Option values are stored as an object with indices as keys
      Object.entries(changes.option1_values).forEach(([index, value]) => {
        const valueIndex = parseInt(index, 10);
        if (!isNaN(valueIndex) && valueIndex >= 0 && valueIndex < input.options[0].values.length) {
          input.options[0].values[valueIndex] = value;
          console.log(`Updated option 1 value at index ${valueIndex} to: ${value}`);
        }
      });
    }
    
    if (changes.option2_values && input.options.length >= 2) {
      // Option values are stored as an object with indices as keys
      Object.entries(changes.option2_values).forEach(([index, value]) => {
        const valueIndex = parseInt(index, 10);
        if (!isNaN(valueIndex) && valueIndex >= 0 && valueIndex < input.options[1].values.length) {
          input.options[1].values[valueIndex] = value;
          console.log(`Updated option 2 value at index ${valueIndex} to: ${value}`);
        }
      });
    }
    
    if (changes.option3_values && input.options.length >= 3) {
      // Option values are stored as an object with indices as keys
      Object.entries(changes.option3_values).forEach(([index, value]) => {
        const valueIndex = parseInt(index, 10);
        if (!isNaN(valueIndex) && valueIndex >= 0 && valueIndex < input.options[2].values.length) {
          input.options[2].values[valueIndex] = value;
          console.log(`Updated option 3 value at index ${valueIndex} to: ${value}`);
        }
      });
    }
    
    // Handle option name updates
    if (changes.option_name_updates && input.options.length > 0) {
      console.log('Applying option name updates:', changes.option_name_updates);
      Object.entries(changes.option_name_updates).forEach(([optionId, newName]) => {
        // Find the option by ID
        const optionIndex = input.options.findIndex(opt => opt.id === optionId);
        if (optionIndex !== -1) {
          console.log(`Updating option name from "${input.options[optionIndex].name}" to "${newName}"`);
          input.options[optionIndex].name = newName;
        }
      });
    }
    
    // Handle option value updates
    if (changes.option_value_updates && input.options.length > 0) {
      console.log('Applying option value updates:', changes.option_value_updates);
      Object.entries(changes.option_value_updates).forEach(([optionId, valueUpdates]) => {
        // Find the option by ID
        const optionIndex = input.options.findIndex(opt => opt.id === optionId);
        if (optionIndex !== -1) {
          Object.entries(valueUpdates).forEach(([oldValue, newValue]) => {
            // Find the value in the option's values array
            const valueIndex = input.options[optionIndex].values.findIndex(v => 
              typeof v === 'string' ? v === oldValue : v.name === oldValue
            );
            
            if (valueIndex !== -1) {
              console.log(`Updating option value from "${oldValue}" to "${newValue}"`);
              
              // Update the value based on its format (string or object with name property)
              if (typeof input.options[optionIndex].values[valueIndex] === 'string') {
                input.options[optionIndex].values[valueIndex] = newValue;
              } else {
                input.options[optionIndex].values[valueIndex].name = newValue;
              }
              
              // Also update any variants that use this option value
              input.variants.forEach(variant => {
                if (variant.options[optionIndex] === oldValue) {
                  console.log(`Updating variant option value from "${oldValue}" to "${newValue}"`);
                  variant.options[optionIndex] = newValue;
                }
              });
            }
          });
        }
      });
    }
  }

  /**
   * Applies metafield changes to product input data
   * @param {object} input - The product input data to modify
   * @param {object} changes - Changes to apply to the product
   * @param {object} product - Original product data (for metafield references)
   */
  applyMetafieldChanges(input, changes, product) {
    if (!changes.metafield_updates) return;
    
    if (!input.metafields) input.metafields = [];
    
    console.log(`Processing ${Object.keys(changes.metafield_updates).length} metafield updates`);
    
    // Create a map of existing metafield types from the product data
    const existingMetafieldTypes = new Map();
    
    // Add types from the original product metafields
    if (product && product.metafields && product.metafields.edges) {
      product.metafields.edges.forEach(edge => {
        if (edge.node && edge.node.namespace && edge.node.key) {
          const key = `${edge.node.namespace}.${edge.node.key}`;
          existingMetafieldTypes.set(key, edge.node.type || 'single_line_text_field');
          console.log(`Found existing metafield type for ${key}: ${edge.node.type || 'single_line_text_field'}`);
        }
      });
    }
    
    Object.entries(changes.metafield_updates).forEach(([metafieldKey, metafieldValue]) => {
      // Handle both ID-based metafields and namespace.key format
      if (metafieldKey.includes('.')) {
        // Handle namespace.key format
        const [namespace, key] = metafieldKey.split('.');
        
        // First check if we have the original type from the product
        let metafieldType = existingMetafieldTypes.get(metafieldKey) || 'single_line_text_field';
        
        // Update existing metafield or add new one
        const existingIndex = input.metafields.findIndex(m => 
          m.namespace === namespace && m.key === key
        );
        
        if (existingIndex >= 0) {
          input.metafields[existingIndex].value = metafieldValue;
          // Keep the existing type
          console.log(`Updated metafield: ${namespace}.${key} = ${metafieldValue} (keeping type: ${input.metafields[existingIndex].type})`);
        } else {
          input.metafields.push({
            namespace: namespace,
            key: key,
            value: metafieldValue,
            type: metafieldType
          });
          console.log(`Added new metafield: ${namespace}.${key} = ${metafieldValue} (type: ${metafieldType})`);
        }
      } else {
        // Handle ID-based metafields (original logic)
        const metafield = product?.metafields?.edges?.find(edge => edge.node.id === metafieldKey)?.node;
        
        if (metafield) {
          // Use the original metafield type
          const metafieldType = metafield.type || 'single_line_text_field';
          
          // Update existing metafield or add new one
          const existingIndex = input.metafields.findIndex(m => 
            m.namespace === metafield.namespace && m.key === metafield.key
          );
          
          if (existingIndex >= 0) {
            input.metafields[existingIndex].value = metafieldValue;
            // Keep the existing type
            console.log(`Updated metafield: ${metafield.namespace}.${metafield.key} = ${metafieldValue} (keeping type: ${input.metafields[existingIndex].type})`);
          } else {
            input.metafields.push({
              namespace: metafield.namespace,
              key: metafield.key,
              value: metafieldValue,
              type: metafieldType
            });
            console.log(`Added updated metafield: ${metafield.namespace}.${metafield.key} = ${metafieldValue} (type: ${metafieldType})`);
          }
        }
      }
    });
  }

  /**
   * Applies variant changes to product input data
   * @param {object} input - The product input data to modify
   * @param {object} changes - Changes to apply to the product
   * @param {object} product - Original product data (for variant references)
   */
  applyVariantChanges(input, changes, product) {
    if (!changes.variant_updates || input.variants.length === 0) return;
    
    // Map variant IDs to their index in the variants array
    const variantMap = {};
    
    if (product?.variants?.edges) {
      product.variants.edges.forEach((edge, index) => {
        variantMap[edge.node.id] = index;
      });
    } else if (product?.variants && Array.isArray(product.variants)) {
      product.variants.forEach((variant, index) => {
        variantMap[variant.id] = index;
      });
    }
    
    // Apply updates to each variant
    Object.entries(changes.variant_updates).forEach(([variantId, updates]) => {
      const variantIndex = variantMap[variantId];
      
      if (variantIndex !== undefined && input.variants[variantIndex]) {
        const originalSku = input.variants[variantIndex].sku;
        
        // Apply each update to the variant
        Object.entries(updates).forEach(([field, value]) => {
          // Skip null or undefined values
          if (value === null || value === undefined) {
            return;
          }
          
          input.variants[variantIndex][field] = value;
          
          // Track SKU changes for image association
          if (field === 'sku' && originalSku && value !== originalSku) {
            console.log(`Tracking SKU change: ${originalSku} -> ${value}`);
            input.skuChangeMap[originalSku] = value;
            
            // If the original SKU had an image association, transfer it to the new SKU
            if (input.skuImageMap[originalSku]) {
              input.skuImageMap[value] = input.skuImageMap[originalSku];
              console.log(`Transferred image association from ${originalSku} to ${value}: ${input.skuImageMap[value]}`);
            }
          }
        });
      }
    });
  }
}
